import React, { useEffect, useMemo, useState } from 'react';
import { adminAPI } from '../../api/admin.api';
import Button from '../../components/common/Button';
import Loader from '../../components/common/Loader';
import { NAV_CATEGORIES } from '../../constants/catalog';
import { useSiteSettings } from '../../context/SiteSettingsContext';
import { imageLooksUsable, normalizeImageUrl } from '../../utils/productImage';
import { HOME_CATEGORY_PRODUCT_LIMIT } from '../../utils/homeProducts';
import './MainPage.css';

const DEFAULT_LIMITED_OFFERS_TITLE = 'Limited Offers';

const OFFER_FIELDS = [
  {
    id: 'watch',
    label: 'Limited watch deal',
    eyebrow: 'Limited watch deal',
    title: 'On all\nwatches',
    description: '',
    href: '/products?category=watches',
    salePercentage: '15',
  },
  {
    id: 'sale',
    label: 'Seasonal sale',
    eyebrow: 'Seasonal sale',
    title: 'Seasonal sale',
    description: '',
    href: '/products',
    salePercentage: '20',
  },
  {
    id: 'gift',
    label: 'Free gift',
    eyebrow: '',
    title: 'Free gift',
    description: 'with every purchase\nover 100₾',
    href: '/products',
    salePercentage: '',
  },
];

const createEmptyCategoryProductIds = () => NAV_CATEGORIES.reduce((map, category) => ({
  ...map,
  [category]: Array.from({ length: HOME_CATEGORY_PRODUCT_LIMIT }, () => ''),
}), {});

const normalizeMainPage = (value = {}) => {
  const sourceOffers = Array.isArray(value.limitedOffers) ? value.limitedOffers : [];
  const sourceOffersById = new Map(sourceOffers
    .filter((offer) => offer?.id)
    .map((offer) => [String(offer.id), offer]));
  const limitedOffers = OFFER_FIELDS.map((offer, index) => ({
    ...offer,
    ...(sourceOffersById.get(offer.id) || sourceOffers[index] || {}),
    id: offer.id,
    label: (sourceOffersById.get(offer.id) || sourceOffers[index])?.label || offer.label,
    eyebrow: (sourceOffersById.get(offer.id) || sourceOffers[index])?.eyebrow ?? offer.eyebrow,
    title: (sourceOffersById.get(offer.id) || sourceOffers[index])?.title || offer.title,
    description: (sourceOffersById.get(offer.id) || sourceOffers[index])?.description ?? offer.description,
    href: (sourceOffersById.get(offer.id) || sourceOffers[index])?.href || offer.href,
    salePercentage: String((sourceOffersById.get(offer.id) || sourceOffers[index])?.salePercentage || ''),
    imageUrl: (sourceOffersById.get(offer.id) || sourceOffers[index])?.imageUrl || '',
  }));
  const categoryProductIds = createEmptyCategoryProductIds();

  NAV_CATEGORIES.forEach((category) => {
    const ids = Array.isArray(value.categoryProductIds?.[category])
      ? value.categoryProductIds[category]
      : [];
    categoryProductIds[category] = Array.from({ length: HOME_CATEGORY_PRODUCT_LIMIT }, (_, index) => ids[index] || '');
  });

  return {
    limitedOffersTitle: value.limitedOffersTitle || DEFAULT_LIMITED_OFFERS_TITLE,
    limitedOffers,
    categoryProductIds,
  };
};

const readFileAsDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onerror = () => reject(new Error('Failed to read file'));
  reader.onload = () => resolve(reader.result);
  reader.readAsDataURL(file);
});

const getProductId = (product) => String(product.id || product._id || '');
const normalizeValue = (value) => String(value || '').trim().toLowerCase();
const titleize = (value) => String(value || '')
  .split(/[-_]/g)
  .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
  .join(' ');

const getProductLabel = (product) => {
  const title = product.title || product.name || 'Product';
  const brand = product.brand ? `${product.brand} - ` : '';
  return `${brand}${title}`;
};

const MainPage = () => {
  const [mainPage, setMainPage] = useState(() => normalizeMainPage());
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingOfferId, setUploadingOfferId] = useState('');
  const { reloadSettings } = useSiteSettings();

  useEffect(() => {
    const load = async () => {
      try {
        const [mainPageData, productsData] = await Promise.all([
          adminAPI.getMainPage(),
          adminAPI.getProducts(),
        ]);
        const productsList = productsData?.items || productsData?.products || productsData?.data || productsData || [];

        setMainPage(normalizeMainPage(mainPageData?.mainPage));
        setProducts(Array.isArray(productsList) ? productsList : []);
      } catch (error) {
        console.error('Failed to load main page settings:', error);
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const productsById = useMemo(() => {
    const map = new Map();
    products.forEach((product) => {
      const id = getProductId(product);
      if (id) {
        map.set(id, product);
      }
    });
    return map;
  }, [products]);

  const getOptionsForCategory = (category) => {
    const selectedIds = mainPage.categoryProductIds[category] || [];
    const selectedProducts = selectedIds.map((id) => productsById.get(String(id))).filter(Boolean);
    const scopedProducts = category === 'presents'
      ? products
      : products.filter((product) => normalizeValue(product.category) === category);
    const optionMap = new Map();

    [...selectedProducts, ...scopedProducts].forEach((product) => {
      const id = getProductId(product);
      if (id) {
        optionMap.set(id, product);
      }
    });

    return Array.from(optionMap.values()).sort((first, second) => getProductLabel(first).localeCompare(getProductLabel(second)));
  };

  const updateOfferImage = (offerIndex, imageUrl) => {
    setMainPage((current) => ({
      ...current,
      limitedOffers: current.limitedOffers.map((offer, index) => (
        index === offerIndex ? { ...offer, imageUrl } : offer
      )),
    }));
  };

  const updateOfferField = (offerIndex, field, value) => {
    setMainPage((current) => ({
      ...current,
      limitedOffers: current.limitedOffers.map((offer, index) => (
        index === offerIndex ? { ...offer, [field]: value } : offer
      )),
    }));
  };

  const uploadOfferImage = async (offerIndex, file) => {
    if (!file) {
      return;
    }

    const offerId = mainPage.limitedOffers[offerIndex]?.id || String(offerIndex);
    setUploadingOfferId(offerId);

    try {
      const base64 = await readFileAsDataUrl(file);
      const { url, absoluteUrl } = await adminAPI.uploadImage({ fileName: file.name, base64 });
      updateOfferImage(offerIndex, url || absoluteUrl);
    } catch (error) {
      console.error('Offer image upload failed:', error);
      alert(error?.response?.data?.message || 'Offer image upload failed');
    } finally {
      setUploadingOfferId('');
    }
  };

  const updateCategoryProduct = (category, index, productId) => {
    setMainPage((current) => ({
      ...current,
      categoryProductIds: {
        ...current.categoryProductIds,
        [category]: current.categoryProductIds[category].map((id, idIndex) => (
          idIndex === index ? productId : id
        )),
      },
    }));
  };

  const handleSave = async () => {
    setSaving(true);

    try {
      const payload = {
        limitedOffersTitle: mainPage.limitedOffersTitle || DEFAULT_LIMITED_OFFERS_TITLE,
        limitedOffers: mainPage.limitedOffers.map((offer) => ({
          id: offer.id,
          label: offer.label || '',
          eyebrow: offer.eyebrow || '',
          title: offer.title || '',
          description: offer.description || '',
          href: offer.href || '',
          salePercentage: Number(offer.salePercentage || 0),
          imageUrl: offer.imageUrl || '',
        })),
        categoryProductIds: NAV_CATEGORIES.reduce((map, category) => ({
          ...map,
          [category]: (mainPage.categoryProductIds[category] || []).filter(Boolean).slice(0, HOME_CATEGORY_PRODUCT_LIMIT),
        }), {}),
      };

      const data = await adminAPI.updateMainPage(payload);
      setMainPage(normalizeMainPage(data?.mainPage || payload));
      await reloadSettings();
      alert('Main page updated');
    } catch (error) {
      console.error('Failed to save main page settings:', error);
      alert(error?.response?.data?.message || 'Failed to save main page settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="admin-main-page">
      <div className="admin-main-page__header">
        <h1>Main Page</h1>
        <p>Choose limited offer text, photos, links, sale values, and the four products shown from each homepage category.</p>
      </div>

      <section className="admin-main-page__section">
        <h2>Limited Offer Cards</h2>

        <label className="admin-main-page__field admin-main-page__field--compact">
          <span>Section title</span>
          <input
            value={mainPage.limitedOffersTitle}
            onChange={(event) => setMainPage((current) => ({
              ...current,
              limitedOffersTitle: event.target.value,
            }))}
          />
        </label>

        <div className="admin-main-page__offers">
          {mainPage.limitedOffers.map((offer, index) => {
            const imageUrl = imageLooksUsable(offer.imageUrl) ? normalizeImageUrl(offer.imageUrl) : '';

            return (
              <article className="admin-main-page__offer" key={offer.id}>
                <div className="admin-main-page__offer-preview">
                  {imageUrl ? <img src={imageUrl} alt="" /> : <span>No photo selected</span>}
                </div>

                <label className="admin-main-page__field">
                  <span>Admin label</span>
                  <input
                    value={offer.label}
                    onChange={(event) => updateOfferField(index, 'label', event.target.value)}
                  />
                </label>

                <label className="admin-main-page__field">
                  <span>Top text</span>
                  <input
                    value={offer.eyebrow}
                    onChange={(event) => updateOfferField(index, 'eyebrow', event.target.value)}
                  />
                </label>

                <label className="admin-main-page__field">
                  <span>Main text</span>
                  <textarea
                    rows={3}
                    value={offer.title}
                    onChange={(event) => updateOfferField(index, 'title', event.target.value)}
                  />
                </label>

                <label className="admin-main-page__field">
                  <span>Extra text</span>
                  <textarea
                    rows={2}
                    value={offer.description}
                    onChange={(event) => updateOfferField(index, 'description', event.target.value)}
                  />
                </label>

                <label className="admin-main-page__field">
                  <span>Sale percentage</span>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={offer.salePercentage}
                    onChange={(event) => updateOfferField(index, 'salePercentage', event.target.value)}
                  />
                </label>

                <label className="admin-main-page__field">
                  <span>Click link</span>
                  <input
                    value={offer.href}
                    onChange={(event) => updateOfferField(index, 'href', event.target.value)}
                  />
                </label>

                <label className="admin-main-page__upload">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(event) => uploadOfferImage(index, event.target.files?.[0])}
                  />
                  <span>{uploadingOfferId === offer.id ? 'Uploading...' : (imageUrl ? 'Replace photo' : 'Upload photo')}</span>
                </label>
                {imageUrl && (
                  <button type="button" onClick={() => updateOfferImage(index, '')}>
                    Remove photo
                  </button>
                )}
              </article>
            );
          })}
        </div>
      </section>

      <section className="admin-main-page__section">
        <h2>Homepage Products</h2>
        <p className="admin-main-page__hint">Select up to 20 products for each category. Selected products appear first in the homepage carousel. Presents can use any product.</p>

        <div className="admin-main-page__categories">
          {NAV_CATEGORIES.map((category) => {
            const options = getOptionsForCategory(category);

            return (
              <article className="admin-main-page__category" key={category}>
                <h3>{titleize(category)}</h3>
                <div className="admin-main-page__selects">
                  {Array.from({ length: HOME_CATEGORY_PRODUCT_LIMIT }, (_, index) => (
                    <label key={`${category}-${index}`}>
                      <span>Product {index + 1}</span>
                      <select
                        value={mainPage.categoryProductIds[category]?.[index] || ''}
                        onChange={(event) => updateCategoryProduct(category, index, event.target.value)}
                      >
                        <option value="">Auto select</option>
                        {options.map((product) => {
                          const id = getProductId(product);
                          return (
                            <option key={id} value={id}>
                              {getProductLabel(product)}
                            </option>
                          );
                        })}
                      </select>
                    </label>
                  ))}
                </div>
              </article>
            );
          })}
        </div>
      </section>

      <div className="admin-main-page__actions">
        <Button type="button" onClick={handleSave} disabled={saving || Boolean(uploadingOfferId)}>
          {saving ? 'Saving...' : 'Save Main Page'}
        </Button>
      </div>
    </div>
  );
};

export default MainPage;
